import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNotificationStore } from '../../services/notifications';
import { useCorporateStore } from '../../services/corporates';
import { doGET } from '../../utils/HttpUtils';
import {
  CreateGeneralNotificationRequest,
  NotificationType,
} from '../../types/notifications';
import { NOTIFICATION_TYPES } from './constants';
import { UserData } from '../../types/user';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type Scope = 'general' | 'corporate';
type GeneralTarget = 'all' | 'selected';

const SendNotificationDialog = ({ isModalOpen, toggleModal, hideAddButton = false }: any) => {
  const { send } = useNotificationStore();
  const { allData: corporates, fetchAll: fetchCorporates } = useCorporateStore();

  const [step, setStep] = useState<1 | 2>(1);
  const [scope, setScope] = useState<Scope>('general');
  const [generalTarget, setGeneralTarget] = useState<GeneralTarget>('all');
  const [corporateId, setCorporateId] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<UserData[]>([]);
  const [userOptions, setUserOptions] = useState<UserData[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [type, setType] = useState<NotificationType>('general');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [referenceType, setReferenceType] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [sending, setSending] = useState(false);

  const handleOpen = () => toggleModal(true);
  const handleClose = () => {
    toggleModal(false);
    setStep(1);
    setScope('general');
    setGeneralTarget('all');
    setCorporateId('');
    setSelectedUsers([]);
    setType('general');
    setTitle('');
    setMessage('');
    setImageUrl('');
    setReferenceType('');
    setReferenceId('');
  };

  useEffect(() => {
    if (isModalOpen) {
      fetchCorporates();
    }
  }, [isModalOpen, fetchCorporates]);

  useEffect(() => {
    if (!isModalOpen || scope !== 'general' || generalTarget !== 'selected') {
      setUserOptions([]);
      return;
    }
    let cancelled = false;
    setUsersLoading(true);
    doGET('/users?forUser=true&limit=100')
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data;
        const users = data?.users ?? data ?? [];
        setUserOptions(Array.isArray(users) ? users : []);
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isModalOpen, scope, generalTarget]);

  const canGoToStep2 = () => {
    if (scope === 'corporate') return !!corporateId;
    if (scope === 'general' && generalTarget === 'selected')
      return selectedUsers.length > 0;
    return true; // general + all users
  };

  const handleNext = () => {
    if (step === 1 && canGoToStep2()) setStep(2);
  };

  const handleBack = () => setStep(1);

  const handleSubmit = async () => {
    if (!title.trim() || !message.trim()) return;
    const payload: CreateGeneralNotificationRequest = {
      type,
      title: title.trim(),
      message: message.trim(),
    };
    if (imageUrl.trim()) payload.imageUrl = imageUrl.trim();
    if (referenceType.trim()) payload.referenceType = referenceType.trim();
    if (referenceId.trim()) payload.referenceId = referenceId.trim();

    if (scope === 'corporate' && corporateId) {
      payload.corporateId = corporateId;
    } else if (scope === 'general' && generalTarget === 'selected' && selectedUsers.length > 0) {
      payload.userIds = selectedUsers.map((u) => u._id).filter(Boolean) as string[];
    }
    // else: no userIds, no corporateId → backend sends to all users

    setSending(true);
    try {
      const result = await send(payload);
      if (result) handleClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="flex-end">
        {!hideAddButton && (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpen}>
            Send notification
          </Button>
        )}
      </Stack>

      <Dialog
        open={isModalOpen}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {step === 1 ? 'Step 1: Scope' : 'Step 2: Notification details'}
        </DialogTitle>
        <DialogContent dividers>
          {step === 1 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Choose who will receive this notification
              </Typography>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  value={scope}
                  onChange={(e) => {
                    setScope(e.target.value as Scope);
                    if (e.target.value === 'general') {
                      setCorporateId('');
                    } else {
                      setGeneralTarget('all');
                      setSelectedUsers([]);
                    }
                  }}
                >
                  <FormControlLabel value="general" control={<Radio />} label="General" />
                  <FormControlLabel value="corporate" control={<Radio />} label="Corporate" />
                </RadioGroup>
              </FormControl>

              {scope === 'corporate' && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel id="notif-corporate-label">Corporate</InputLabel>
                  <Select
                    labelId="notif-corporate-label"
                    label="Corporate"
                    value={corporateId}
                    onChange={(e) => setCorporateId(e.target.value)}
                  >
                    {corporates.map((c: any) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.name ?? c._id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {scope === 'general' && (
                <Stack sx={{ mt: 2, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Target
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      value={generalTarget}
                      onChange={(e) => setGeneralTarget(e.target.value as GeneralTarget)}
                    >
                      <FormControlLabel value="all" control={<Radio />} label="All users" />
                      <FormControlLabel value="selected" control={<Radio />} label="Selected users" />
                    </RadioGroup>
                  </FormControl>
                  {generalTarget === 'selected' && (
                    <Autocomplete
                      multiple
                      size="small"
                      options={userOptions}
                      getOptionLabel={(opt) => opt.name || opt.email || opt._id || ''}
                      value={selectedUsers}
                      onChange={(_, newValue) => setSelectedUsers(newValue)}
                      loading={usersLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select users"
                          placeholder="Search users..."
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {usersLoading ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{ mt: 1, minWidth: 280 }}
                    />
                  )}
                </Stack>
              )}
            </Box>
          )}

          {step === 2 && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                select
                size="small"
                label="Type"
                value={type}
                onChange={(e) => setType(e.target.value as NotificationType)}
                fullWidth
                required
              >
                {NOTIFICATION_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                fullWidth
                inputProps={{ maxLength: 200 }}
              />
              <TextField
                size="small"
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                fullWidth
                multiline
                rows={3}
                inputProps={{ maxLength: 1000 }}
              />
              <TextField
                size="small"
                label="Image URL (optional)"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                helperText="Public HTTPS image URL, under 1 MB. Leave blank for text only."
                fullWidth
              />
              <TextField
                size="small"
                label="Reference type (optional)"
                value={referenceType}
                onChange={(e) => setReferenceType(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="Reference ID (optional)"
                value={referenceId}
                onChange={(e) => setReferenceId(e.target.value)}
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {step === 2 ? (
            <>
              <Button onClick={handleBack} disabled={sending}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!title.trim() || !message.trim() || sending}
              >
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canGoToStep2()}
              >
                Next
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SendNotificationDialog;
